"use client";
import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  FileText,
  Bot,
  BookOpen,
  Layers,
  GitBranch,
  Lock,
  Archive,
  Plus,
  FileStack,
  FolderPlus,
  Settings,
  LogOut,
  FileBadge,
} from "lucide-react";
import { PROJECT_FORM_FIELDS, SIDEBAR_FOLDERS } from "./dashboard/constant";
import { DropdownButton } from "@/UI/Button";
import Modal from "@/UI/CenterScreenModal";
import DynamicForm from "./dashboard/DynamicForm";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import { image } from "framer-motion/client";
import SidebarComponentDropdown from "./dashboard/SidebarDropdownContent";
import { exit } from "process";
import { AllModels } from "@/data/TemplateModels";

// Icon mapping for folders
const FOLDER_ICONS = {
  eraserbot: Bot,
  references: BookOpen,
  templates: Layers,
  github: GitBranch,
  private: Lock,
  archive: Archive,
} as const;

interface SidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [isTeamExpanded, setIsTeamExpanded] = useState(false);
  const [isFoldersExpanded, setIsFoldersExpanded] = useState(true);
  const [formValues, setFormValues] = useState<Record<string, string>>({
    name: "",
    description: "",
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const { user, loading, logout } = useAuth();
  const dropdownRef = useRef<HTMLButtonElement>(null);

  // Template options
  const templateOptions: string[] = Object.keys(AllModels);

useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsTeamExpanded(false);
    }
  };
  if (isTeamExpanded) {
    document.addEventListener("mousedown", handleClickOutside);
  }
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [isTeamExpanded]);


  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    // setError('');

    if (!formValues.name.trim()) {
      // setError('Project name cannot be empty');
      return;
    }

    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });

      if (res.ok) {
        setFormValues({ name: "", description: "" });
        setIsCreateOpen(false);
        // fetchProjects();
      } else {
        const errData = await res.json();
        // setError(errData.error || 'Failed to create project');
      }
    } catch (err) {
      // setError('Failed to create project');
      console.error(err);
    }
  };

  const handleFormChange = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for template selection - creates project step by step
  const handleTemplateSelect = async (templateName: string) => {
    try {
      setIsCreatingFromTemplate(true);
      
      // Step 1: Get template models
      const templateModels = AllModels[templateName as keyof typeof AllModels];
      
      if (!templateModels || templateModels.length === 0) {
        console.error("Template not found or empty");
        setIsCreatingFromTemplate(false);
        return;
      }

      // Step 2: Clean template models - remove undefined/null fields that cause validation issues
      const cleanedModels = templateModels.map((model: any) => {
        const cleanedModel: any = {
          name: model.name,
          properties: model.properties.map((prop: any) => {
            const cleanedProp: any = {};
            
            // Process each property field - explicitly handle enum
            for (const key in prop) {
              if (!prop.hasOwnProperty(key)) continue;
              
              const value = prop[key];
              
              // Special handling for enum - ONLY include if it's a valid non-empty array
              if (key === 'enum') {
                if (Array.isArray(value) && value.length > 0) {
                  cleanedProp[key] = value;
                }
                // Completely skip enum if it's undefined, null, empty, or not an array
                continue;
              }
              
              // For all other fields, include if not undefined or null
              if (value !== undefined && value !== null) {
                cleanedProp[key] = value;
              }
            }
            
            return cleanedProp;
          })
        };
        
        // Only include options if it exists
        if (model.options) {
          cleanedModel.options = model.options;
        }
        
        return cleanedModel;
      });

      // Step 2.5: Deep clean using JSON serialization to remove any remaining undefined/null values
      // This ensures the data is completely clean before sending to API
      const deeplyCleanedModels = JSON.parse(JSON.stringify(cleanedModels));

      // Step 3: Create project with deeply cleaned template models
      const projectData: any = {
        description: "",
        models: deeplyCleanedModels,
      };
      // Omit name field - backend will set default
      
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(projectData),
      });

      if (!res.ok) {
        let errorMessage = 'Failed to create project from template';
        try {
          const errData = await res.json();
          errorMessage = errData.error || errorMessage;
        } catch (err) {
          // Response might not be JSON, use status text or default message
          errorMessage = res.statusText || errorMessage;
        }
        console.error(errorMessage);
        setIsCreatingFromTemplate(false);
        return;
      }

      // Step 4: Get the created project ID
      const data = await res.json();
      
      // Step 5: Redirect to the workspace
      if (data.id) {
        window.location.href = `/workspace/${data.id}`;
      } else {
        console.error("Project created but no ID returned");
        setIsCreatingFromTemplate(false);
      }
    } catch (err) {
      console.error('Failed to create project from template', err);
      setIsCreatingFromTemplate(false);
    }
  };

  const items = [
    {
      title: "Settings",
      onClick: () => console.log("Team Settings clicked"),
      image: <Settings className="w-4 h-4"/>,
      shortcut: "⌥ S"
    },
    {
      title: "Logout",
      onClick: () => {
        logout();
        console.log("Sign Out clicked");
      },
      shortcut: "⌥ S",
      image: <LogOut className="w-4 h-4"/>,
    },
  ];

  return (
    <aside
      className={`h-screen bg-primaryBackground border-r border-primaryBorder flex flex-col transition-all duration-300 ease-in-out pb-2 px-2 ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Team Section */}
      <div className="p-4 border-b border-primaryBorder">
        <button
          // onClick={() => setIsTeamExpanded(!isTeamExpanded)}
          ref={dropdownRef}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[var(--foreground)] text-[var(--primaryText)] hover:text-[var(--hoverText)] transition-colors"
          onClick={(e) => {e.stopPropagation(); setIsTeamExpanded((prev) => !prev)}}
          >
          <>
            <div
              className="flex items-center gap-2 flex-1 min-w-0 relative "
            >
              <div className="w-6 h-6 bg-gradient-to-br text-white from-blue-500 to-purple-600 rounded flex items-center justify-center text-sm font-semibold  flex-shrink-0">
                {user ? user.name.charAt(0).toUpperCase() : "T"}
              </div>
              <span className="font-medium  truncate">
                {user ? `${user.name}'s Team` : "Team Name"}
              </span>
              {isTeamExpanded && (
                <SidebarComponentDropdown isTeamExpanded setIsTeamExpanded={setIsTeamExpanded} items={items} userInfo={user}  />
              )}
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                isTeamExpanded ? "" : "-rotate-90"
              }`}
              onClick={(e) => {e.stopPropagation(); setIsTeamExpanded((prev) => !prev)}}
            />
          </>
        </button>
      </div>

      {/* All Files */}
      <div className=" py-3">
        <Link
          href={"/dashboard"}
          className="w-full flex items-center gap-3 px-3 py-2 border rounded bg-[var(--secondaryBackground)] border-[var(--hoverBackground)] text-[var(--primaryText)] font-medium "
        >
          <FileBadge className="w-4 h-4 flex-shrink-0" />
          <>
            <span className="flex-1 text-left">All Files</span>
            <span className="text-xs text-[var(--primaryText)]">A</span>
          </>
        </Link>
      </div>

      {/* Team Folders */}
      <div className=" pb-3 flex flex-col justify-between h-full ">
        <div className="flex items-center justify-between mb-2 bg-[var(--primaryBackground)] rounded px-2" aria-disabled={true} title="Coming Soon">
          <>
            <button
              onClick={() => setIsFoldersExpanded(!isFoldersExpanded)}
              className="flex items-center gap-2 group"
            >
              <span className="text-xs font-semibold text-[var(--primaryText)]/50 uppercase tracking-wider">
                Team Folders
              </span>
            </button>
            <button className="text-[var(--secondaryText)] hover:bg-[var(--primaryBackground)] rounded hover:text-[var(--primaryText)] transition-colors">
              <Plus className="w-6 h-6 p-1" />
            </button>
          </>
        </div>

        {/* <div className="">
          {SIDEBAR_FOLDERS.map((folder) => {
            const IconComponent =
              FOLDER_ICONS[folder.id as keyof typeof FOLDER_ICONS] || FileText;

            return (
              <button
                key={folder.id}
                className={`w-full flex items-center gap-3 px-3 py-[3px] rounded-lg transition-all  duration-200 ${
                  folder.variant === "upgrade"
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-[var(--hoverBackground)] hover:text-[var(--hoverText)] hover:scale-[1.02]"
                }`}
                disabled={folder.variant === "upgrade"}
                title={isCollapsed ? folder.label : undefined}
              >
                <IconComponent className="w-4 h-4 text-[var(--primaryText)] flex-shrink-0" />
                <>
                  <span className="text-sm text-[var(--primaryText)] flex-1 text-left truncate">
                    {folder.label}
                  </span>
                  {folder.badge && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-[var(--hoverBackground)] text-[var(--secondaryText)] rounded font-medium whitespace-nowrap">
                      {folder.badge}
                    </span>
                  )}
                  {folder.variant === "upgrade" && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-neutral rounded text-[var(--primaryText)] font-medium whitespace-nowrap">
                      UPGRADE
                    </span>
                  )}
                  {folder.shortcut &&
                    !folder.badge &&
                    folder.variant !== "upgrade" && (
                      <span className="text-xs text-[var(--primaryText)]">
                        {folder.shortcut}
                      </span>
                    )}
                </>
              </button>
            );
          })}
        </div> */}
      </div>
      <DropdownButton
        title="New File"
        onClick={() => setIsCreateOpen(true)}
        items={templateOptions.map((template: string) => ({
          title: template,
          onClick: () => handleTemplateSelect(template),
          image: <FileText className="w-4 h-4" />,
        }))}
        disabled={isCreatingFromTemplate}
      />

      {/* Create Project Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New Project"
      >
        <DynamicForm
          fields={PROJECT_FORM_FIELDS}
          values={formValues}
          onChange={handleFormChange}
          onSubmit={handleCreateProject}
          // error={error}
          submitLabel="Create"
          onCancel={() => setIsCreateOpen(false)}
        />
      </Modal>
    </aside>
  );
};

export default Sidebar;
